import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import type { EvaluationAnswererType, EvaluationForm } from "../evaluationFormsApi";
import { updateEvaluationFormApi } from "../evaluationFormsApi";

export const CATEGORY_OPTIONS = [
  { value: "LOJA", label: "Loja" },
  { value: "FREELANCER", label: "Freelancer" },
  { value: "NR1", label: "NR1 (pesquisa anônima)" },
];

export const ANSWERER_OPTIONS: { value: EvaluationAnswererType; label: string }[] = [
  { value: "ADMIN", label: "Administrador" },
  { value: "SUPERVISOR", label: "Supervisor" },
  { value: "SUPERVISOR_DE_CAMPO", label: "Supervisor de campo" },
  { value: "FREELANCER", label: "Freelancer" },
];

type FormInfoDialogProps = {
  form: EvaluationForm;
  onClose: () => void;
  onSaved: () => void;
  /** DP: a categoria fica travada em NR1 (05/11/2026). */
  nr1Only?: boolean;
};

/**
 * Edita nome, categoria e quem responde — os dados de cabeçalho que o
 * `UpdateEvaluationFormModal` editava. NR1 é anônimo: não pede tipo de usuário.
 */
const FormInfoDialog: React.FC<FormInfoDialogProps> = ({ form, onClose, onSaved, nr1Only = false }) => {
  const [title, setTitle] = useState(form.title ?? "");
  const [category, setCategory] = useState(form.category ?? "LOJA");
  const [answerers, setAnswerers] = useState<EvaluationAnswererType[]>(form.allowedAnswererTypes ?? []);
  const [saving, setSaving] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  const isAnonymous = category === "NR1";
  const canSave = title.trim().length > 0 && (isAnonymous || answerers.length > 0) && !saving;

  useEffect(() => {
    titleRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, saving]);

  const toggleAnswerer = (value: EvaluationAnswererType) =>
    setAnswerers((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    try {
      setSaving(true);
      await updateEvaluationFormApi(form.id, {
        title: title.trim(),
        category,
        allowedAnswererTypes: isAnonymous ? [] : answerers,
      });
      toast.success("Formulário atualizado.");
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao salvar formulário.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ecoDialog__overlay" role="presentation" onClick={() => !saving && onClose()}>
      <form
        className="ecoDialog fForms__infoDialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fInfo-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h2 id="fInfo-title" className="ecoDialog__title">
          Dados do formulário
        </h2>

        <div className="ecoField">
          <label htmlFor="fInfo-title-input">Nome</label>
          <input
            ref={titleRef}
            id="fInfo-title-input"
            className="ecoInput"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving}
          />
        </div>

        <div className="ecoField">
          <label htmlFor="fInfo-category">Categoria</label>
          <select
            id="fInfo-category"
            className="ecoSelect"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={saving || nr1Only}
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {!CATEGORY_OPTIONS.some((o) => o.value === category) ? <option value={category}>{category}</option> : null}
          </select>
        </div>

        {isAnonymous ? (
          <p className="ecoDialog__text">
            Formulário NR1: respondido anonimamente por link público de uso único, sem restrição por tipo de usuário.
          </p>
        ) : (
          <fieldset className="fForms__answerers">
            <legend className="ecoLabel">Quem pode responder</legend>
            {ANSWERER_OPTIONS.map((o) => (
              <label key={o.value} className="qPanel__check">
                <input
                  type="checkbox"
                  checked={answerers.includes(o.value)}
                  onChange={() => toggleAnswerer(o.value)}
                  disabled={saving}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </fieldset>
        )}

        <div className="ecoDialog__actions">
          <button type="button" className="ecoBtn" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="ecoBtn ecoBtn--primary" disabled={!canSave}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormInfoDialog;
